<template>
  <tile class="opc-zmone">
    <foldable v-model="isExpanded" :title="title" :isRemoveWrapAriaLabelledby="true">
      <div slot="head" class="opc-zm-base-card__head">
        <div class="opc-zm-base-card__head-left">
          <img class="opc-zm-base-card__logo" :src="logo" :alt="title" tabindex="-1" />
        </div>
        <div class="opc-zm-base-card__head-right">
          <h2 class="opc-zm-base-card__name text-capitalize">
            {{ title }}
            <i class="zm-icon-success main-icon" aria-hidden="true"></i>
          </h2>
          <div class="opc-zm-base-card__view-detail-button">
            <zm-button
              data-testid="checkout-button-134"
              class="opc-zm-base-card__link"
              type="link"
              @click="handlePlanDetailsClick"
              v-if="isExpanded && showPlanDetail"
            >
              {{ $t('billing.plan_details') }}
            </zm-button>
          </div>
          <div class="opc-zm-base-card__collapse opc-zm-base-card__collapse--reset" v-if="!isExpanded && formPlan">
            <!-- // FREE TRIAL - money is 0 -->
            {{
              $t('billing.zone.desc1', [
                form.selectedNumber,
                $money(0),
                $t(`billing.common.per_billing_cycle_user.${form.billingCycle}`),
              ])
            }}
          </div>
          <div class="opc-zmone__trial-desc" v-if="isExpanded">
            <p class="opc-zmone__desc-text">
              <img src="@/assets/plans/checked_checkboxes-green.svg" alt="Include" />
              {{ $t('billing.zone.trial.desc1') }}
            </p>
            <p class="opc-zmone__desc-text">
              <img src="@/assets/plans/checked_checkboxes-green.svg" alt="" />
              {{ $t('billing.zone.trial.desc2') }}
              <img src="@/assets/plans/sparkle-new.svg" class="opc-zmone__asterisk" alt="" />
            </p>
            <p class="opc-zmone__desc-text">
              <img src="@/assets/plans/checked_checkboxes-green.svg" alt="" />
              {{ $t('billing.zone.trial.desc3') }}
            </p>
          </div>
        </div>
      </div>
      <slot name="zo-main">
        <div class="opc-zmone__main opc-zmone__free-trial-main">
          <zm-form ref="zmoneForm" label-position="top" class="opc-config-form">
            <zm-form-item :label="$t('billing.zone.trial.select_free_trial')">
              <cycle-radio-group
                v-model="form.billingCycle"
                :plans="availableFreeTrialPlans"
                :action="switchBillingCycle"
                aria-label="Zoom Workplace Free Trial Billing Cycle"
                class="opc-card-radio"
              >
                <zm-tooltip
                  v-for="plan in availableFreeTrialPlans"
                  :key="plan.billing_cycle"
                  :disabled="!(typeof billingCycleDisabled(plan) === 'string')"
                >
                  <div slot="content" v-html="billingCycleDisabled(plan) || ''" />
                  <zm-radio
                    data-testid="checkout-radio-135"
                    :label="plan.billing_cycle"
                    :disabled="!!billingCycleDisabled(plan)"
                    class="opc-zmone__radio"
                  >
                    <p class="opc-zmone__radio-title text-capitalize">
                      {{ $t('billing.trial.trial_days_free', [plan.trialDays]) }}
                    </p>
                    <p class="opc-zmone__free-trial-billing-cycle-name text-capitalize opc-price-bill-text">
                      {{ $t(`billing.billing_cycle.${plan.billing_cycle}`) }}
                    </p>
                    <p class="opc-product-price-container" tabindex="-1" :aria-label="ariaPriceOfMonthForLabel(plan)">
                      <span v-for="(value, key, index) in getPriceOfMonth(plan.amount, plan.billing_cycle, { forceDiff: true })"
                            :key="index"
                            :class="[`opc-product-price-container__${key}`, useSmallSize(value) ? 'opc-product-price-container__small' : '']"
                            :style="value.symbol && value.place === 'end' ? { order: 1 } : {}"
                            aria-hidden="true"
                            >
                        {{ value.symbol ? value.symbol: value }}
                      </span>
                    </p>
                    <p class="opc-product-price-bill-text-desc">
                       <span v-html="$t('billing.trial.unit_suffix', [`${getMergedBilledText(plan.billing_cycle, plan.amount)}`])"></span><priceVATTip />
                    </p>
                  </zm-radio>
                </zm-tooltip>
              </cycle-radio-group>
            </zm-form-item>
            <preview-payment />
          </zm-form>
        </div>
      </slot>
    </foldable>
  </tile>
</template>
<script>
import baseMixins from '@/plans/ZOOM_MEETING/config/base';
import { BILLING_CYCLE } from '@/common/constants';
import { $desc } from '@/components/service/config-desc';
import { getTitleNameI18nKey } from '../common/utils';
import { getPlanUnitWithCycle } from '@/common/product-unit';
import { isEmptyVal } from '@/utils';

export default {
  name: 'BuyTrial',
  mixins: [baseMixins],
  data() {
    return {
      showPlanDetail: true,
    };
  },
  computed: {
    title() {
      return this.$t('billing.trial.title', [this.$t(getTitleNameI18nKey(this.form?.category))]);
    },
    nextChargeAmount() {
      const map = {
        [BILLING_CYCLE.MONTHLY]:
          this.currentPlans.find((plan) => plan.billing_cycle === BILLING_CYCLE.MONTHLY)?.amount || 0,
        [BILLING_CYCLE.ANNUAL]:
          this.currentPlans.find((plan) => plan.billing_cycle === BILLING_CYCLE.ANNUAL)?.amount || 0,
      };
      return map;
    },
    availableFreeTrialPlans() {
      return this.currentPlans?.filter((p) => p?.isOnlineFreeTrial && !isEmptyVal((p?.trialDays)));
    }
  },
  methods: {
    descRender() {
      const nextChargeAmountWithFormat = {
        [BILLING_CYCLE.MONTHLY]: `${this.$money(this.nextChargeAmount[BILLING_CYCLE.MONTHLY], {
          forceDiff: true,
        })}${getPlanUnitWithCycle(BILLING_CYCLE.MONTHLY)}`,
        [BILLING_CYCLE.ANNUAL]: `${this.$money(this.nextChargeAmount[BILLING_CYCLE.ANNUAL], {
          forceDiff: true,
        })}${getPlanUnitWithCycle(BILLING_CYCLE.ANNUAL)}`,
      };
      return (
        <p class="opc-zmone__buy-free-trial-terms">
          {this.$t('billing.zone.trial.trial_charge_terms_desc', [
            nextChargeAmountWithFormat[BILLING_CYCLE.MONTHLY],
            nextChargeAmountWithFormat[BILLING_CYCLE.ANNUAL],
          ])}
        </p>
      );
    },
  },
  mounted() {
    $desc(this.descRender());
  },
};
</script>
<style lang="scss">
@import '@zoom/billing-responsive/styles/responsive.scss';
.opc-zmone {
  &__trial-desc {
    margin: 14px 0;
  }
  &__desc-text {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    color: #00053d;
    font-size: 14px;
    font-weight: 500;
    line-height: 21px;
    margin: 4px 0;
    img {
      display: inline-block;
      margin-right: 4px;
    }
  }
  &__asterisk {
    display: inline-block;
    margin-left: 2px;
    height: 14px;
  }
  &__free-trial-billing-cycle-name {
    font-size: 22px;
    font-weight: 700;
    line-height: 33px;
    .opc-price-save-count {
      vertical-align: middle;
    }
  }
  &__free-trial-main {
    margin-top: 0;
  }
  &__free-trial-price {
    font-size: 12px;
    font-weight: 500;
    line-height: 18px;
    color: #515079;
    span:first-child {
      font-weight: 700;
    }
  }
}

.config-desc-slot:has(.opc-zmone__buy-free-trial-terms) {
  margin-top: 24px;
}
</style>
